import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, MessageCircle, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const WhatsAppScriptsPanel = () => {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  const fetchScripts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/v4/admin/whatsapp-scripts`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load reply templates');
      const data = await res.json();
      setScripts(data.scripts || []);
    } catch (error) {
      console.error('Scripts fetch error:', error);
      toast.error('Failed to load reply templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScripts();
  }, [fetchScripts]);

  const copyToClipboard = async (script) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(script.message);
      } else {
        // Fallback for older browsers / insecure contexts
        const ta = document.createElement('textarea');
        ta.value = script.message;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success(`"${script.title}" copied — paste into WhatsApp`);
      setCopiedId(script.id);
      setTimeout(() => setCopiedId((id) => (id === script.id ? null : id)), 2000);
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Could not copy to clipboard');
    }
  };

  if (loading) {
    return (
      <Card data-testid="v5-whatsapp-scripts-panel">
        <CardContent className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="v5-whatsapp-scripts-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-[#25D366]" />
          WhatsApp Reply Templates
        </CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          Standardized responses for qualifying incoming WhatsApp leads.
          Click <strong>Copy</strong> on any template, then paste into your
          WhatsApp reply.
        </p>
        <div className="mt-2">
          <a
            href="https://web.whatsapp.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-[#25D366] hover:underline"
            data-testid="v5-open-whatsapp-web"
          >
            Open WhatsApp Web →
          </a>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          {scripts.map((script) => {
            const isCopied = copiedId === script.id;
            return (
              <div
                key={script.id}
                className="rounded-lg border border-gray-200 bg-gray-50/50 overflow-hidden"
                data-testid={`v5-script-card-${script.key}`}
              >
                <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-200 bg-white">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900">
                      {script.title}
                    </h3>
                    {script.description && (
                      <p className="text-xs text-gray-500 mt-1">
                        {script.description}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant={isCopied ? 'default' : 'outline'}
                    className={
                      isCopied
                        ? 'gap-1.5 bg-green-600 hover:bg-green-700 text-white shrink-0'
                        : 'gap-1.5 shrink-0'
                    }
                    onClick={() => copyToClipboard(script)}
                    data-testid={`v5-copy-script-${script.key}`}
                  >
                    {isCopied ? (
                      <>
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <pre className="p-4 text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed max-h-80 overflow-y-auto">
                  {script.message}
                </pre>
              </div>
            );
          })}
        </div>
        <div className="mt-6 text-xs text-gray-500">
          <Badge variant="outline" className="mr-2">
            Note
          </Badge>
          Edit templates in <code className="px-1 py-0.5 rounded bg-gray-100">/app/backend/whatsapp_scripts.py</code>
          {' '} — changes ship via code review, not ad-hoc edits.
        </div>
      </CardContent>
    </Card>
  );
};

export default WhatsAppScriptsPanel;
